// Formatting and the small pieces every Eval Suite tab shares.
//
// THE RULE THE EVAL SCREEN IS BUILT AROUND: never print an aggregate without the
// n it was computed from, and never render a MISSING INSTRUMENT as a number. 0%
// and 100% are both lies when nothing looked. Every block that can be
// uninstrumented carries a `note` from the server, and when that note is present
// the card refuses to print a value and says what it is waiting for instead.
//
// A second rule shows up in the grid: a cell computed over fewer than
// MIN_CELL_N turns keeps its number and loses its colour. A 0% cell over two
// turns is not a finding, and colouring it red sends someone to fix a segment
// that was never measured.

import { type ReactNode } from "react";
import { ApiError } from "../api";
import { Banner, SkeletonRows } from "../ui";
import { type MatrixCell, type EvalTurn } from "./types";

/** Below this many turns a cell keeps its number and loses its colour. */
export const MIN_CELL_N = 5;

export function dash(value: unknown): string {
  const s = String(value ?? "").trim();
  return s === "" ? "—" : s;
}

export function num(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** A rate in [0,1] as a percent. NEVER coerces null to 0 — a missing measurement reads "—". */
export function fmtPct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

export function fmtInt(value: unknown): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString();
}

export function fmtNum3(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(Number(value))
    ? "—"
    : Number(value).toFixed(3);
}

export function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(Number(ms))) return "—";
  const n = Number(ms);
  if (n >= 1000) return `${(n / 1000).toFixed(2)}s`;
  return `${n.toFixed(n >= 100 ? 0 : 1)}ms`;
}

export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * The store writes naive UTC timestamps. Appending the Z is what stops every run
 * reading as if it happened in the operator's own offset.
 */
export function toEpoch(ts: string | null | undefined): number {
  const raw = String(ts ?? "").trim();
  if (!raw) return 0;
  const iso = raw.endsWith("Z") || raw.includes("+") ? raw : `${raw}Z`;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

export function fmtTime(ts: string | null | undefined): string {
  const t = toEpoch(ts);
  return t ? new Date(t).toLocaleString() : "—";
}

export function shortId(id: unknown, keep = 14): string {
  const s = String(id ?? "");
  if (!s) return "—";
  return s.length > keep ? `${s.slice(0, keep)}…` : s;
}

/** The corpus's own axes — the `eval_turns` column names the server allowlists. */
export const AXES: Array<{ key: string; label: string }> = [
  { key: "scam_strata", label: "Scam strata" },
  { key: "user_strata", label: "User strata" },
  { key: "scam_cluster", label: "Scam cluster" },
  { key: "user_cluster", label: "User cluster" },
  { key: "archetype", label: "Journey archetype" },
  { key: "exposure", label: "Exposure" },
  { key: "affect", label: "Affect" },
  { key: "turn_act", label: "Turn act" },
  { key: "saturation", label: "Saturation" },
  { key: "certainty", label: "Certainty" },
  { key: "continuity", label: "Continuity" },
  { key: "lang", label: "Language" },
  { key: "turn_owner", label: "Turn owner" },
  { key: "grader", label: "Grader" },
  { key: "suite", label: "Suite" },
];

export function axisLabel(key: string | null | undefined): string {
  const k = String(key ?? "");
  return AXES.find((a) => a.key === k)?.label ?? (k || "—");
}

export type Bucket =
  | "b-empty"
  | "b-unmeasured"
  | "b-thin"
  | "b-good"
  | "b-ok"
  | "b-warn"
  | "b-poor"
  | "b-bad";

export function passBucket(cell: MatrixCell | null): Bucket {
  if (!cell || !cell.n) return "b-empty";
  if (cell.n < MIN_CELL_N) return "b-thin";
  const r = num(cell.pass_rate);
  if (r >= 0.99) return "b-good";
  if (r >= 0.9) return "b-ok";
  if (r >= 0.75) return "b-warn";
  if (r >= 0.5) return "b-poor";
  return "b-bad";
}

/**
 * The waste scale is INVERTED (more is worse) and refuses in two extra places:
 * a cell with no judged turn is unmeasured rather than 0% waste, and a cell
 * judged on fewer than MIN_CELL_N turns keeps its number and loses its colour.
 * 0% avoidable is an excellent result; it must be reachable only by measurement.
 */
export function avoidableBucket(cell: MatrixCell | null): Bucket {
  if (!cell || !cell.n) return "b-empty";
  const judged = num(cell.necessity_n);
  if (cell.avoidable_share === null || cell.avoidable_share === undefined || !judged) {
    return "b-unmeasured";
  }
  if (judged < MIN_CELL_N) return "b-thin";
  const s = Number(cell.avoidable_share);
  if (s >= 0.5) return "b-bad";
  if (s >= 0.25) return "b-poor";
  if (s >= 0.1) return "b-warn";
  if (s > 0) return "b-ok";
  return "b-good";
}

/** Same buckets as the grid, as an ink colour for tables and cards. */
export function rateColor(rate: number | null | undefined, n: number = MIN_CELL_N): string {
  if (rate === null || rate === undefined || num(n) < MIN_CELL_N) return "var(--text-3)";
  const r = Number(rate);
  if (r >= 0.9) return "var(--success)";
  if (r >= 0.75) return "var(--warning)";
  if (r >= 0.5) return "var(--serious)";
  return "var(--critical)";
}

export function wasteColor(share: number | null | undefined): string {
  if (share === null || share === undefined) return "var(--text-3)";
  const s = Number(share);
  if (s >= 0.25) return "var(--critical)";
  if (s >= 0.1) return "var(--warning)";
  return "var(--success)";
}

export function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// ============================================================ small pieces

export function ErrorBanner({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <Banner tone="critical" traceId={api?.traceId}>
      {what}: {errMessage(error, "request failed")}
    </Banner>
  );
}

/** Error → skeleton → content, in that order, for every list on these screens. */
export function Loadable({
  loading,
  error,
  what,
  cols = 4,
  rows = 4,
  children,
}: {
  loading: boolean;
  error: ApiError | null;
  what: string;
  cols?: number;
  rows?: number;
  children: ReactNode;
}) {
  if (error) return <ErrorBanner error={error} what={what} />;
  if (loading) return <SkeletonRows n={rows} cols={cols} />;
  return <>{children}</>;
}

/**
 * One headline metric, with an explicit THIRD state beside value and loading:
 * NOT INSTRUMENTED. When `note` is set the card prints no number at all — the
 * alternative (0, or 100%) is the most flattering possible rendering of a
 * missing instrument, and this screen is read as evidence.
 */
export function MetricCard({
  label,
  value,
  sub,
  sub2,
  note,
  hint,
  tone = "default",
}: {
  label: string;
  value?: string | null;
  sub?: string;
  sub2?: string;
  note?: string | null;
  hint?: string | null;
  tone?: "default" | "good" | "warn" | "bad";
}) {
  const color =
    tone === "good"
      ? "var(--success)"
      : tone === "warn"
        ? "var(--warning)"
        : tone === "bad"
          ? "var(--critical)"
          : "var(--ink)";
  return (
    <div className="stat" title={hint ?? note ?? undefined}>
      <div className="label">{label}</div>
      {note ? (
        <>
          <div className="value" style={{ fontSize: 15, color: "var(--warning)" }}>
            not instrumented
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            {note}
          </div>
        </>
      ) : (
        <>
          <div className="value" style={{ color }}>
            {value ?? "—"}
          </div>
          {sub && (
            <div className="muted" style={{ fontSize: 11 }}>
              {sub}
            </div>
          )}
          {sub2 && (
            <div className="muted" style={{ fontSize: 11 }}>
              {sub2}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Magnitude bar. Never a status — the colour is supplied by the caller. */
export function Meter({ value, color = "var(--info)" }: { value: number; color?: string }) {
  const pct = Math.max(0, Math.min(100, value * 100));
  return (
    <div className="meter">
      <span style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export function Card({
  title,
  note,
  actions,
  children,
}: {
  title: string;
  note?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      <div className="card-head" style={{ padding: 0, border: 0, marginBottom: "var(--sp-3)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{title}</h3>
          {note && (
            <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {note}
            </p>
          )}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function TurnBodies({ turn }: { turn: EvalTurn }) {
  return (
    <div className="grid c2">
      <div>
        <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
          user
        </div>
        <div className="prewrap" style={{ fontSize: 12 }}>
          {dash(turn.user_text)}
        </div>
      </div>
      <div>
        <div className="muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
          bot
        </div>
        <div className="prewrap" style={{ fontSize: 12 }}>
          {dash(turn.reply_text)}
        </div>
      </div>
    </div>
  );
}

/** A neutral chip. `Tag` in ui.tsx takes a remove handler; this one is read-only. */
export function Tagish({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>;
}
